import api from './api';
import logger from '../utils/logger';

// Use the central api instance from services/api.js. That instance has the
// configured baseURL, the request interceptor that attaches the access
// token, and the response interceptor that handles 401 → token refresh,
// rate-limit retry, and centralized error normalization. Creating a
// private axios instance here would silently bypass all of that.

/**
 * Permission API Service
 * Handles all permission-related API calls following the backend API reference
 */
export class PermissionAPI {
  constructor() {
    this.baseURL = '/permissions';
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes as per backend spec
  }

  // =================== ROLE MANAGEMENT ===================

  /**
   * List all roles
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip
   * @param {number} params.limit - Maximum records to return
   * @param {boolean} params.include_inactive - Include inactive roles
   * @returns {Promise<Array>} Array of role objects
   */
  async getRoles(params = {}) {
    try {
      logger.info('PermissionAPI: Fetching roles', params);
      const response = await api.get(`${this.baseURL}/roles`, { params });
      logger.success('PermissionAPI: Roles fetched successfully', { count: response.data?.length });
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to fetch roles', error);
      throw error;
    }
  }

  /**
   * Create a new role
   * @param {Object} roleData - Role creation data
   * @param {string} roleData.name - Role name (unique)
   * @param {string} roleData.display_name - Display name
   * @param {string} roleData.description - Role description
   * @param {number|null} roleData.parent_id - Parent role ID
   * @param {boolean} roleData.is_system - Whether it's a system role
   * @param {Array<number>} roleData.permission_ids - Array of permission IDs
   * @returns {Promise<Object>} Created role object
   */
  async createRole(roleData) {
    try {
      logger.info('PermissionAPI: Creating role', { name: roleData.name });
      const response = await api.post(`${this.baseURL}/roles`, roleData);
      logger.success('PermissionAPI: Role created successfully', { id: response.data?.id });
      this.clearCache(); // Clear cache after modification
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to create role', error);
      throw error;
    }
  }

  /**
   * Update an existing role
   * @param {number} roleId - Role ID to update
   * @param {Object} updateData - Role update data
   * @returns {Promise<Object>} Updated role object
   */
  async updateRole(roleId, updateData) {
    try {
      logger.info('PermissionAPI: Updating role', { roleId, updateData });
      const response = await api.put(`${this.baseURL}/roles/${roleId}`, updateData);
      logger.success('PermissionAPI: Role updated successfully', { roleId });
      this.clearCache(); // Clear cache after modification
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to update role', error);
      throw error;
    }
  }

  /**
   * Delete a role (soft delete)
   * @param {number} roleId - Role ID to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteRole(roleId) {
    try {
      logger.info('PermissionAPI: Deleting role', { roleId });
      const response = await api.delete(`${this.baseURL}/roles/${roleId}`);
      logger.success('PermissionAPI: Role deleted successfully', { roleId });
      this.clearCache(); // Clear cache after modification
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to delete role', error);
      throw error;
    }
  }

  // =================== PERMISSION MANAGEMENT ===================

  /**
   * List all permissions
   * @param {Object} params - Query parameters
   * @param {string} params.resource - Filter by resource name
   * @param {string} params.action - Filter by action name
   * @param {boolean} params.include_inactive - Include inactive permissions
   * @returns {Promise<Array>} Array of permission objects
   */
  async getPermissions(params = {}) {
    try {
      logger.info('PermissionAPI: Fetching permissions', params);
      const response = await api.get(`${this.baseURL}/permissions`, { params });
      logger.success('PermissionAPI: Permissions fetched successfully', { count: response.data?.length });
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to fetch permissions', error);
      throw error;
    }
  }

  /**
   * Create a new permission
   * @param {Object} permissionData - Permission creation data
   * @param {string} permissionData.name - Permission name (resource:action format)
   * @param {string} permissionData.resource - Resource name
   * @param {string} permissionData.action - Action name
   * @param {string} permissionData.description - Permission description
   * @returns {Promise<Object>} Created permission object
   */
  async createPermission(permissionData) {
    try {
      logger.info('PermissionAPI: Creating permission', { name: permissionData.name });
      const response = await api.post(`${this.baseURL}/permissions`, permissionData);
      logger.success('PermissionAPI: Permission created successfully', { id: response.data?.id });
      this.clearCache(); // Clear cache after modification
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to create permission', error);
      throw error;
    }
  }

  // =================== USER ROLE ASSIGNMENT ===================

  /**
   * Assign a role to a user
   * @param {number} userId - User ID
   * @param {Object} roleData - Role assignment data
   * @param {number} roleData.role_id - Role ID to assign
   * @param {string|null} roleData.expires_at - Expiration date (ISO format)
   * @returns {Promise<Object>} Assignment confirmation
   */
  async assignRoleToUser(userId, roleData) {
    try {
      logger.info('PermissionAPI: Assigning role to user', { userId, roleId: roleData.role_id });
      const response = await api.post(`${this.baseURL}/users/${userId}/roles`, roleData);
      logger.success('PermissionAPI: Role assigned successfully', { userId, roleId: roleData.role_id });
      this.clearUserPermissionCache(userId);
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to assign role to user', error);
      throw error;
    }
  }

  /**
   * Remove a role from a user
   * @param {number} userId - User ID
   * @param {number} roleId - Role ID to remove
   * @param {string} reason - Reason for removal (optional)
   * @returns {Promise<Object>} Removal confirmation
   */
  async removeRoleFromUser(userId, roleId, reason = null) {
    try {
      logger.info('PermissionAPI: Removing role from user', { userId, roleId, reason });
      const params = reason ? { reason } : {};
      const response = await api.delete(`${this.baseURL}/users/${userId}/roles/${roleId}`, { params });
      logger.success('PermissionAPI: Role removed successfully', { userId, roleId });
      this.clearUserPermissionCache(userId);
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to remove role from user', error);
      throw error;
    }
  }

  /**
   * Get user's roles
   * @param {number} userId - User ID
   * @returns {Promise<Array>} Array of role objects
   */
  async getUserRoles(userId) {
    try {
      logger.info('PermissionAPI: Fetching user roles', { userId });
      const response = await api.get(`${this.baseURL}/users/${userId}/roles`);
      logger.success('PermissionAPI: User roles fetched successfully', { userId, count: response.data?.length });
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to fetch user roles', error);
      throw error;
    }
  }

  /**
   * Get user's all permissions (including from roles)
   * @param {number} userId - User ID
   * @returns {Promise<Array>} Array of permission objects
   */
  async getUserPermissions(userId) {
    try {
      logger.info('PermissionAPI: Fetching user permissions', { userId });
      const response = await api.get(`${this.baseURL}/users/${userId}/permissions`);
      logger.success('PermissionAPI: User permissions fetched successfully', { userId, count: response.data?.length });
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to fetch user permissions', error);
      throw error;
    }
  }

  // =================== PERMISSION CHECKING ===================

  /**
   * Check if current user has a specific permission
   * @param {string} resource - Resource name
   * @param {string} action - Action name
   * @returns {Promise<boolean>} Whether user has permission
   */
  async checkPermission(resource, action) {
    const cacheKey = `perm_${resource}_${action}`;
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        logger.debug('PermissionAPI: Permission check from cache', { resource, action, hasPermission: cached.value });
        return cached.value;
      }
    }

    try {
      logger.info('PermissionAPI: Checking permission', { resource, action });
      const response = await api.post(`${this.baseURL}/check`, { resource, action });
      const hasPermission = response.data?.has_permission || false;
      
      // Cache the result
      this.cache.set(cacheKey, {
        value: hasPermission,
        timestamp: Date.now()
      });
      
      logger.success('PermissionAPI: Permission checked successfully', { resource, action, hasPermission });
      return hasPermission;
    } catch (error) {
      logger.error('PermissionAPI: Failed to check permission', error);
      // Default to false on error for security
      return false;
    }
  }

  /**
   * Check multiple permissions at once
   * @param {Array<Object>} permissions - Array of {resource, action} objects
   * @returns {Promise<Object>} Object with permission results
   */
  async checkPermissionsBulk(permissions) {
    try {
      logger.info('PermissionAPI: Checking multiple permissions', { count: permissions.length });
      const response = await api.post(`${this.baseURL}/check-bulk`, { permissions });
      const results = response.data?.results || {};
      
      // Cache individual results
      Object.entries(results).forEach(([permKey, hasPermission]) => {
        this.cache.set(`perm_bulk_${permKey}`, {
          value: hasPermission,
          timestamp: Date.now()
        });
      });
      
      logger.success('PermissionAPI: Bulk permission check completed', { count: Object.keys(results).length });
      return results;
    } catch (error) {
      logger.error('PermissionAPI: Failed to check permissions bulk', error);
      // Return false for all permissions on error
      const results = {};
      permissions.forEach(perm => {
        results[`${perm.resource}:${perm.action}`] = false;
      });
      return results;
    }
  }

  // =================== AUDIT LOGS ===================

  /**
   * Get permission audit logs
   * @param {Object} params - Query parameters
   * @param {number} params.user_id - Filter by user who made changes
   * @param {number} params.target_user_id - Filter by affected user
   * @param {string} params.action - Filter by action type
   * @param {string} params.resource_type - Filter by resource type
   * @param {string} params.start_date - Filter from date
   * @param {string} params.end_date - Filter to date
   * @param {number} params.skip - Pagination offset
   * @param {number} params.limit - Page size
   * @returns {Promise<Array>} Array of audit log objects
   */
  async getAuditLogs(params = {}) {
    try {
      logger.info('PermissionAPI: Fetching audit logs', params);
      const response = await api.get(`${this.baseURL}/audit-logs`, { params });
      logger.success('PermissionAPI: Audit logs fetched successfully', { count: response.data?.length });
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to fetch audit logs', error);
      throw error;
    }
  }

  // =================== CACHE MANAGEMENT ===================

  /**
   * Clear permission cache
   * @param {number|null} userId - Clear cache for specific user only
   * @returns {Promise<Object>} Cache clear confirmation
   */
  async clearServerCache(userId = null) {
    try {
      logger.info('PermissionAPI: Clearing server cache', { userId });
      const params = userId ? { user_id: userId } : {};
      const response = await api.post(`${this.baseURL}/cache/clear`, {}, { params });
      logger.success('PermissionAPI: Server cache cleared successfully', { userId });
      this.clearCache(); // Also clear local cache
      return response.data;
    } catch (error) {
      logger.error('PermissionAPI: Failed to clear server cache', error);
      throw error;
    }
  }

  /**
   * Clear local cache
   * @param {string|null} pattern - Clear specific cache pattern (optional)
   */
  clearCache(pattern = null) {
    if (pattern) {
      // Clear specific pattern
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
      logger.debug('PermissionAPI: Cleared local cache pattern', { pattern });
    } else {
      // Clear all cache
      this.cache.clear();
      logger.debug('PermissionAPI: Cleared all local cache');
    }
  }

  /**
   * Clear permission cache for specific user
   * @param {number} userId - User ID
   */
  clearUserPermissionCache(userId) {
    this.clearCache(`user_${userId}`);
    this.clearCache('perm_'); // Clear all permission checks
  }

  // =================== UTILITY METHODS ===================

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    
    for (const [, value] of this.cache.entries()) {
      if (now - value.timestamp < this.cacheTimeout) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    }
    
    return {
      totalEntries: this.cache.size,
      validEntries,
      expiredEntries,
      cacheTimeout: this.cacheTimeout
    };
  }

  /**
   * Clean expired cache entries
   */
  cleanExpiredCache() {
    const now = Date.now();
    const expiredKeys = [];
    
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp >= this.cacheTimeout) {
        expiredKeys.push(key);
      }
    }
    
    expiredKeys.forEach(key => this.cache.delete(key));
    
    if (expiredKeys.length > 0) {
      logger.debug('PermissionAPI: Cleaned expired cache entries', { count: expiredKeys.length });
    }
  }
}

// Create and export singleton instance
export const permissionAPI = new PermissionAPI();

// Clean expired cache every 10 minutes
setInterval(() => {
  permissionAPI.cleanExpiredCache();
}, 10 * 60 * 1000);

export default permissionAPI;